import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, throwError } from 'rxjs';
import { EmployeeProfileEntity } from '../../domain/entities/employee-profile.entity';
import { Employee360Data } from '../../domain/models/employee-360-data.model';
import { Employee360Api } from '../api/employee-360.api';
import { Employee360Mapper } from '../mappers/employee-360.mapper';

export type { Employee360Data };

@Injectable({ providedIn: 'root' })
export class Employee360Repository {
  private readonly _api = inject(Employee360Api);
  private readonly _mapper = inject(Employee360Mapper);

  /**
   * Searches employees by name or email.
   * Returns an empty list for blank/too-short terms (min 2 chars).
   */
  public searchEmployees(term: string): Observable<EmployeeProfileEntity[]> {
    if (term.trim().length < 2) {
      return of([]);
    }
    return this._api
      .searchEmployees(term)
      .pipe(map((dtos) => dtos.map((dto) => this._mapper.toProfileEntity(dto))));
  }

  /**
   * Retrieves an employee's profile and their case history sorted by
   * created date descending (most recent first).
   * Resolves to null when the employee is not found (404).
   */
  public getEmployee360(userId: string): Observable<Employee360Data | null> {
    return forkJoin({
      profile: this._api.getEmployee(userId).pipe(
        catchError((err: HttpErrorResponse) =>
          err.status === 404 ? of(null) : throwError(() => err),
        ),
      ),
      cases: this._api.getEmployeeCases(userId).pipe(catchError(() => of([]))),
    }).pipe(
      map(({ profile, cases }) => {
        if (!profile) return null;

        const history = (cases ?? [])
          .map((dto) => this._mapper.toHistoryEntity(dto))
          .sort((a, b) => b.createdDate.localeCompare(a.createdDate));

        return {
          profile: this._mapper.toProfileEntity(profile),
          history,
        };
      }),
    );
  }
}
